import { derivePoGrnState } from "@/pages/InvoiceManagement/utils/poGrnAggregation";
import type { PODetailsResponse } from "../api/invoiceQueries";

/**
 * Derives the three amounts shown in ThreeWayMatchCard from the PO details API response.
 *
 * Uses derivePoGrnState for GRN aggregation (dedup by grn_id, consistent with the
 * PO details page).  Returns null for any amount that cannot be determined:
 *   - poAmount null → no po_list entry
 *   - totalInvoiceAmount null → no invoices in the list
 *   - grnAmount null → no GRN allocations across any invoice
 *
 * ₹0 is a valid amount and is returned as 0, not coerced to null.
 */
export function deriveThreeWayMatchAmounts(poData: PODetailsResponse | undefined): {
  poAmount: number | null;
  totalInvoiceAmount: number | null;
  grnAmount: number | null;
} {
  const firstPo = poData?.po_list?.[0];
  if (!firstPo) {
    return { poAmount: null, totalInvoiceAmount: null, grnAmount: null };
  }

  const invoices = firstPo.invoices ?? [];
  const totalInvoiceAmount =
    invoices.length > 0
      ? invoices.reduce((sum, inv) => sum + (inv.invoice_value ?? 0), 0)
      : null;

  const { grnRows, totalGrnValue } = derivePoGrnState(invoices);

  return {
    poAmount: firstPo.order_value ?? null,
    totalInvoiceAmount,
    grnAmount: grnRows.length > 0 ? totalGrnValue : null,
  };
}
